/**
 * Una seccion del home como una sola fila que corre de costado.
 *
 * POR QUE UNA FILA Y NO LA GRILLA
 * -------------------------------
 * En el home cada seccion era una grilla: con doce iPhone y ocho Mac, el
 * cliente tenia que bajar tres pantallas para llegar a la seccion siguiente.
 * Una fila por seccion deja ver de un vistazo todo lo que vende el local, y el
 * que quiere mas de algo lo corre de costado. Al elegir una categoria o buscar
 * se vuelve a la grilla: ahi se vino a recorrer todo.
 *
 * EN EL TELEFONO SE DESLIZA, EN LA PC HAY FLECHAS
 * -----------------------------------------------
 * La ultima tarjeta queda cortada en el borde: es lo que avisa que hay mas.
 * Con mouse una fila sin barra no se descubre, asi que en la PC aparecen dos
 * flechas, cada una solo si hay algo para ese lado.
 */
import { Children, useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

export function FilaDeslizable({
  children,
  chico = false,
  etiqueta,
}: {
  children: ReactNode;
  /** Tarjetas chicas (accesorios): entran mas por pantalla. */
  chico?: boolean;
  /** El nombre de la seccion, para las flechas. */
  etiqueta: string;
}): JSX.Element {
  const fila = useRef<HTMLDivElement>(null);
  const [bordes, setBordes] = useState({ izquierda: false, derecha: false });

  const medir = useCallback(() => {
    const el = fila.current;
    if (!el) return;
    setBordes({
      izquierda: el.scrollLeft > 4,
      derecha: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    const el = fila.current;
    if (!el) return;
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [medir]);

  function correr(hacia: 1 | -1): void {
    const el = fila.current;
    if (!el) return;
    el.scrollBy({ left: hacia * el.clientWidth * 0.9, behavior: "smooth" });
  }

  return (
    <div className="relative">
      <div
        ref={fila}
        onScroll={medir}
        // El `-mx-5` cancela el `px-5` de la seccion para que la fila llegue al
        // borde de la pantalla; el `px-5` y el `scroll-px-5` de adentro la
        // alinean con el titulo. Sin el padding de la seccion, correria la
        // pagina entera de costado.
        className={cn(
          "-mx-5 flex snap-x snap-mandatory scroll-px-5 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "md:mx-0 md:scroll-px-0 md:px-0",
          chico ? "gap-2.5 md:gap-4" : "gap-3.5 md:gap-5",
        )}
      >
        {Children.map(children, (hijo) => (
          <div
            className={cn(
              "shrink-0 snap-start",
              chico
                ? "w-[30%] md:w-[calc((100%-4*16px)/5)] lg:w-[calc((100%-5*16px)/6)]"
                : "w-[44%] md:w-[calc((100%-2*20px)/3.3)] lg:w-[calc((100%-3*20px)/4.3)] xl:w-[calc((100%-4*20px)/5.3)]",
            )}
          >
            {hijo}
          </div>
        ))}
      </div>

      <Flecha
        lado="izquierda"
        visible={bordes.izquierda}
        etiqueta={`Ver los anteriores de ${etiqueta}`}
        onClick={() => correr(-1)}
      />
      <Flecha
        lado="derecha"
        visible={bordes.derecha}
        etiqueta={`Ver más de ${etiqueta}`}
        onClick={() => correr(1)}
      />
    </div>
  );
}

function Flecha({
  lado,
  visible,
  etiqueta,
  onClick,
}: {
  lado: "izquierda" | "derecha";
  visible: boolean;
  etiqueta: string;
  onClick: () => void;
}): JSX.Element | null {
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={etiqueta}
      className={cn(
        // Solo en la PC: en el telefono se desliza con el dedo.
        "absolute top-[30%] z-10 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#E0E0E0] bg-white text-[#0A0A0A] shadow-md transition-colors hover:border-[#0A0A0A] md:flex",
        lado === "izquierda" ? "-left-4" : "-right-4",
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="size-5"
      >
        <path d={lado === "izquierda" ? "m15 6-6 6 6 6" : "m9 6 6 6-6 6"} />
      </svg>
    </button>
  );
}
